import { useCallback, useEffect, useState, type ReactElement } from 'react';
import { Link, useNavigate } from 'react-router';
import { ChevronLeft, Plus } from 'lucide-react';
import {
  PAYMENT_MODES,
  formatHoldMinutes,
  hourMinutes,
  settingsProblems,
  type PaymentMode,
  type VenueSettings,
  type VenueSettingsUpdate,
  type ZoneSetting,
} from '@kratispot/shared';
import { Toasts, useToasts } from '@/components/toasts';
import { Button } from '@/components/ui/button';
import { ApiError, fetchVenueSettings, saveVenueSettings } from '@/lib/api';
import { centsOf, currencySymbolOf, majorOf, wholeOf } from '@/lib/format';
import { useTranslation, type CopyKey } from '@/lib/i18n';
import { cn } from '@/lib/utils';

// Every hour of the venue's own day; the pilot's 9–18 is its data, not our rule.
const HOURS = Array.from({ length: 24 }, (_, hour) => hour);

const STEPS = [30, 60] as const;

const PAYMENT_COPY: Readonly<Record<PaymentMode, CopyKey>> = {
  full: 'payFull',
  hold_fee: 'payHoldFee',
  none: 'payNone',
};

// The map is the only editor of the staff-only flag; this opens it there. §11
export const SELECT_QUERY = '?select=1';

type Draft = VenueSettingsUpdate;

export function draftOf(settings: VenueSettings): Draft {
  return {
    hold: settings.hold,
    paymentMode: settings.paymentMode,
    cancellation: settings.cancellation,
    zones: settings.zones.map((zone) => ({ ...zone, names: { ...zone.names } })),
  };
}

// A fresh zone is named after its key until the owner types something better.
export function blankZone(languages: readonly string[], taken: readonly string[]): ZoneSetting {
  let index = taken.length + 1;
  while (taken.includes(`zone-${index}`)) index += 1;
  const key = `zone-${index}`;
  return {
    key,
    names: Object.fromEntries(languages.map((code) => [code, key])),
    dayPriceCents: 0,
    holdPerHourCents: 0,
  };
}

export function numberingOf(summary: VenueSettings['summary']): string | null {
  if (summary.numberFrom === null || summary.numberTo === null) return null;
  return `${summary.numberFrom}–${summary.numberTo}`;
}

function Card({ value, label }: { value: string; label: string }): ReactElement {
  return (
    <div className="flex flex-col gap-0.5 rounded-xl border bg-card p-3">
      <b className="text-lg leading-tight font-extrabold">{value}</b>
      <span className="text-xs text-muted-foreground">{label}</span>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactElement[] | ReactElement }) {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-xs font-bold tracking-wide text-muted-foreground uppercase">{title}</h3>
      <div className="flex flex-col divide-y rounded-xl border bg-card">{children}</div>
    </section>
  );
}

function Field({ label, children }: { label: string; children: ReactElement }): ReactElement {
  return (
    <label className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
      <span className="min-w-0 flex-1">{label}</span>
      {children}
    </label>
  );
}

const INPUT = 'w-24 rounded-lg border px-2 py-1 text-end text-base';

// The symbol sits inside the box, so it never drifts away from the amount. §9
function Money({
  symbol,
  label,
  cents,
  invalid,
  onPick,
}: {
  symbol: string;
  label: string;
  cents: number;
  invalid: boolean;
  onPick: (cents: number) => void;
}): ReactElement {
  return (
    <span className={cn(MONEY_BOX, invalid && AT_FAULT)}>
      <span aria-hidden>{symbol}</span>
      <input
        type="number"
        min={0}
        step="0.5"
        className={MONEY_INPUT}
        aria-label={label}
        aria-invalid={invalid}
        value={majorOf(cents)}
        onChange={(event) => onPick(centsOf(event.target.value))}
      />
    </span>
  );
}

const SELECT = 'rounded-lg border px-2 py-1 text-base';

const NAME = 'min-w-28 flex-1 rounded-lg border px-2 py-1 text-base';

// A refused save marks the controls it named, since a toast cannot point. §15
const AT_FAULT = 'border-red-500 bg-red-50 text-red-900';

const MONEY_BOX = 'inline-flex items-center gap-1 rounded-lg border px-2 py-1';

// Four figures, a dot and two cents is the widest price anyone charges. §9
const MONEY_INPUT = 'w-16 border-0 bg-transparent p-0 text-base outline-none';

// Owner-only: pricing, the hold window, the payment mode and the zones. §4, §18
export function SettingsPage({ venueId }: { venueId: string }): ReactElement {
  const { t, language } = useTranslation();
  const navigate = useNavigate();
  const [settings, setSettings] = useState<VenueSettings | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [phase, setPhase] = useState<'loading' | 'ready' | 'failed'>('loading');
  const [busy, setBusy] = useState(false);
  const { toasts, push } = useToasts();
  const [problems, setProblems] = useState<readonly string[]>([]);

  const load = useCallback(
    (signal?: AbortSignal) =>
      fetchVenueSettings(venueId, signal)
        .then((loaded) => {
          setSettings(loaded);
          setDraft(draftOf(loaded));
          setPhase('ready');
        })
        .catch(() => {
          if (!signal?.aborted) setPhase('failed');
        }),
    [venueId],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  if (phase !== 'ready' || settings === null || draft === null)
    return (
      <p
        role={phase === 'failed' ? 'alert' : undefined}
        className="p-6 text-center text-sm text-muted-foreground"
      >
        {t(phase === 'failed' ? 'errorGeneric' : 'loading')}
      </p>
    );

  // Narrowed once, so the handlers never re-check what the guard above proved.
  const current: Draft = draft;
  const languages = settings.languages;
  // Money reads as money: the venue's own symbol stands by each amount. §9
  const symbol = currencySymbolOf(settings.currency, language);

  // Marks follow the edits once a save has named them, and never before.
  const edit = (change: Partial<Draft>): void => {
    const next = { ...draft, ...change };
    setDraft(next);
    if (problems.length > 0) setProblems(settingsProblems(next, languages));
  };

  const wrong = (path: string): boolean => problems.includes(path);

  const mark = (path: string, base: string) => ({
    className: cn(base, wrong(path) && AT_FAULT),
    'aria-invalid': wrong(path),
  });

  const editZone = (key: string, change: Partial<ZoneSetting>): void =>
    edit({ zones: draft.zones.map((zone) => (zone.key === key ? { ...zone, ...change } : zone)) });

  async function save(): Promise<void> {
    const wrong = settingsProblems(current, languages);
    setProblems(wrong);
    if (wrong.length > 0) return push(t('settingsInvalid'), 'bg-red-500');
    setBusy(true);
    try {
      const saved = await saveVenueSettings(venueId, current);
      setSettings(saved);
      setDraft(draftOf(saved));
      setProblems([]);
      push(t('settingsSaved'), 'bg-emerald-600');
    } catch (error) {
      const refused = error instanceof ApiError && error.status === 400;
      push(t(refused ? 'settingsInvalid' : 'settingsFailed'), 'bg-red-500');
    } finally {
      setBusy(false);
    }
  }

  const numbering = numberingOf(settings.summary);

  return (
    <section className="mx-auto flex w-full max-w-2xl flex-col gap-4 p-4">
      <div className="flex items-center gap-2">
        <Button asChild variant="outline" size="sm">
          <Link to="/">
            <ChevronLeft className="size-4" aria-hidden />
            {t('back')}
          </Link>
        </Button>
        <h2 className="truncate text-base font-bold">{t('settings')}</h2>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Card value={String(settings.summary.spots)} label={t('totalSpots')} />
        <Card
          value={`${settings.summary.gridRows} × ${settings.summary.gridCols}`}
          label={t('layoutSize')}
        />
        <Card value={numbering ?? t('noSpotsYet')} label={t('numbering')} />
        {/* The map is the only editor of the flag; this card is its shortcut. §11 */}
        <div className="flex flex-col items-start gap-1.5 rounded-xl border bg-card p-3">
          <span className="flex items-baseline gap-2">
            <b className="text-lg leading-tight font-extrabold">{settings.summary.staffOnly}</b>
            <span className="text-xs text-muted-foreground">★ {t('staffOnlySpots')}</span>
          </span>
          <Button size="sm" variant="outline" onClick={() => void navigate(`/${SELECT_QUERY}`)}>
            {t('manageOnMap')}
          </Button>
        </div>
      </div>

      <Section title={t('holdSettings')}>
        <Field label={t('holdFreeUntil')}>
          <select
            {...mark('hold.includedHour', SELECT)}
            aria-label={t('holdFreeUntil')}
            value={draft.hold.includedHour}
            onChange={(event) =>
              edit({ hold: { ...draft.hold, includedHour: Number(event.target.value) } })
            }
          >
            {HOURS.map((hour) => (
              <option key={hour} value={hour}>
                {formatHoldMinutes(hourMinutes(hour))}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('holdLatest')}>
          <select
            {...mark('hold.latestHour', SELECT)}
            aria-label={t('holdLatest')}
            value={draft.hold.latestHour}
            onChange={(event) =>
              edit({ hold: { ...draft.hold, latestHour: Number(event.target.value) } })
            }
          >
            {HOURS.map((hour) => (
              <option key={hour} value={hour}>
                {formatHoldMinutes(hourMinutes(hour))}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('holdStep')}>
          <select
            {...mark('hold.stepMinutes', SELECT)}
            aria-label={t('holdStep')}
            value={draft.hold.stepMinutes}
            onChange={(event) =>
              edit({
                hold: { ...draft.hold, stepMinutes: Number(event.target.value) as 30 | 60 },
              })
            }
          >
            {STEPS.map((step) => (
              <option key={step} value={step}>
                {step} {t('minutesShort')}
              </option>
            ))}
          </select>
        </Field>
      </Section>

      <Section title={`${t('pricing')} · ${settings.currency}`}>
        {draft.zones.length === 0 ? (
          <p className="px-3 py-3 text-sm text-muted-foreground">{t('noZonesYet')}</p>
        ) : (
          <>
            {draft.zones.map((zone, index) => (
              <div key={zone.key} className="flex flex-col gap-2 p-3">
                <span className="flex flex-wrap gap-2">
                  {languages.map((code) => (
                    <input
                      key={code}
                      {...mark(`zones.${index}.names.${code}`, NAME)}
                      aria-label={`${zone.key} — ${code}`}
                      value={zone.names[code] ?? ''}
                      onChange={(event) =>
                        editZone(zone.key, { names: { ...zone.names, [code]: event.target.value } })
                      }
                    />
                  ))}
                </span>
                <span className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  <Money
                    symbol={symbol}
                    label={`${zone.key} — ${t('perDayLabel')}`}
                    cents={zone.dayPriceCents}
                    invalid={wrong(`zones.${index}.dayPriceCents`)}
                    onPick={(cents) => editZone(zone.key, { dayPriceCents: cents })}
                  />
                  {t('perDayLabel')}
                  <Money
                    symbol={symbol}
                    label={`${zone.key} — ${t('perHourLabel')}`}
                    cents={zone.holdPerHourCents}
                    invalid={wrong(`zones.${index}.holdPerHourCents`)}
                    onPick={(cents) => editZone(zone.key, { holdPerHourCents: cents })}
                  />
                  {t('perHourLabel')}
                </span>
              </div>
            ))}
          </>
        )}
        <span className="p-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              edit({
                zones: [
                  ...draft.zones,
                  blankZone(
                    settings.languages,
                    draft.zones.map((zone) => zone.key),
                  ),
                ],
              })
            }
          >
            <Plus className="size-4" aria-hidden />
            {t('addZone')}
          </Button>
        </span>
      </Section>

      <Section title={t('paymentTitle')}>
        <Field label={t('paymentTitle')}>
          <select
            className={SELECT}
            aria-label={t('paymentTitle')}
            value={draft.paymentMode}
            onChange={(event) => edit({ paymentMode: event.target.value as PaymentMode })}
          >
            {PAYMENT_MODES.map((mode) => (
              <option key={mode} value={mode}>
                {t(PAYMENT_COPY[mode])}
              </option>
            ))}
          </select>
        </Field>
        <p className="px-3 py-2 text-xs text-muted-foreground">{t('paymentNote')}</p>
      </Section>

      <Section title={t('cancelPolicy')}>
        <Field label={t('cancelFull')}>
          <input
            type="number"
            min={0}
            {...mark('cancellation.fullHours', INPUT)}
            aria-label={t('cancelFull')}
            value={draft.cancellation.fullHours}
            onChange={(event) =>
              edit({
                cancellation: { ...draft.cancellation, fullHours: wholeOf(event.target.value) },
              })
            }
          />
        </Field>
        <Field label={t('cancelPartial')}>
          <input
            type="number"
            min={0}
            max={100}
            {...mark('cancellation.partialPct', INPUT)}
            aria-label={t('cancelPartial')}
            value={draft.cancellation.partialPct}
            onChange={(event) =>
              edit({
                cancellation: { ...draft.cancellation, partialPct: wholeOf(event.target.value) },
              })
            }
          />
        </Field>
        <Field label={t('cancelNone')}>
          <input
            type="number"
            min={0}
            {...mark('cancellation.noneHours', INPUT)}
            aria-label={t('cancelNone')}
            value={draft.cancellation.noneHours}
            onChange={(event) =>
              edit({
                cancellation: { ...draft.cancellation, noneHours: wholeOf(event.target.value) },
              })
            }
          />
        </Field>
      </Section>

      <div className="flex flex-wrap items-center gap-3">
        <Button disabled={busy} onClick={() => void save()}>
          {t('saveChanges')}
        </Button>
        <Button asChild variant="outline">
          <Link to="/layout">{t('layoutEditor')}</Link>
        </Button>
      </div>
      <Toasts toasts={toasts} undoLabel={t('undo')} />
    </section>
  );
}
